// Step 2 B3 — Agentic "Ask me anything" hook. Mirrors mobile/services/ask.ts.
//
// Calls the real `POST /chat/ask` endpoint, served by the shared
// ApiFunction (Jotly.Web Minimal API) and answered by the agentic retrieval
// loop in the backend.
//
// If `VITE_ASK_MOCK=1` is set in the build env, the mock is used instead —
// useful for offline iteration on the UI without a live backend.

import { useMutation } from '@tanstack/react-query';
import { askChat, type ChatAskResponse, type Citation } from './client';

/**
 * A single citation surfaced beneath the synthesised answer. Tap on the UI
 * side routes to the underlying note / task / event detail.
 */
// 'item' is the unified capture/dump source type (per ADR-0009 — captures are
// embedded now, so recall hands back dumps too). 'task' is kept for back-compat.
export type AskCitationSource = 'note' | 'item' | 'task' | 'event';

export type AskCitation = {
  sourceType: AskCitationSource;
  id: string;
  title: string;
  snippet: string;
};

export type AskResponse = {
  answer: string;
  citations: AskCitation[];
};

// DEV-gated so the mock path (and its fixture data) is constant-folded out of
// production bundles — `import.meta.env.DEV` is replaced with `false` at
// build time and the whole branch tree-shakes away.
const USE_MOCK = import.meta.env.DEV && import.meta.env.VITE_ASK_MOCK === '1';

const MOCK_DELAY_MS = 700;
const MOCK_CITATIONS: AskCitation[] = [
  {
    sourceType: 'note',
    id: 'note_01HZX0SAMPLE',
    title: 'Bathroom renovation — planning',
    snippet:
      'Tiles: matte porcelain, 600x300. Underfloor heating quote from Phil due by end of month.',
  },
  {
    sourceType: 'task',
    id: 'task_01HZX1SAMPLE',
    title: 'Call Phil for the heating quote',
    snippet: 'Sarah asked for an update — phone call, weekday 10am.',
  },
  {
    sourceType: 'event',
    id: 'event_01HZX2SAMPLE',
    title: 'Bathroom site visit',
    snippet: 'Thursday 14:00 · with Phil at home.',
  },
];

async function askMock(question: string): Promise<AskResponse> {
  await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY_MS));
  const trimmed = question.trim();
  const subject = trimmed.length > 0 ? trimmed : 'that';
  return {
    answer:
      `Here's what I found about ${subject}. Sarah asked you to chase Phil for the heating quote, ` +
      `and there's a bathroom-renovation note with the tile spec. You've also got a site visit on ` +
      `Thursday afternoon.`,
    citations: MOCK_CITATIONS,
  };
}

const KNOWN_SOURCES: AskCitationSource[] = ['note', 'item', 'task', 'event'];

function normaliseCitations(citations: Citation[]): AskCitation[] {
  return citations
    .filter((c): c is Citation & { sourceType: AskCitationSource } =>
      (KNOWN_SOURCES as string[]).includes(c.sourceType),
    )
    .map((c) => ({
      sourceType: c.sourceType,
      id: c.id,
      title: c.title,
      snippet: typeof c.snippet === 'string' ? c.snippet : '',
    }));
}

/**
 * Call the agentic retrieval backend (or the mock when the build is in
 * `VITE_ASK_MOCK=1` mode). Returns the answer + citations.
 */
export async function askQuestion(question: string): Promise<AskResponse> {
  if (USE_MOCK) return askMock(question);

  const result: ChatAskResponse = await askChat(question);
  return {
    answer: result.answer ?? '',
    citations: normaliseCitations(result.citations ?? []),
  };
}

/**
 * TanStack Query mutation hook for the Ask surface. Surface `.mutate(question)`
 * to fire the request and `.data` / `.error` to render the response.
 */
export function useAsk() {
  return useMutation<AskResponse, Error, string>({
    mutationFn: (question: string) => askQuestion(question),
  });
}
